/**
 * Year-end giving statements adapter.
 *
 * The pure aggregation (grouping by giving unit, totals, household labels) lives
 * in @glorify-llc/accounting-core and is shared with Relay (ADR-006). This
 * file keeps the Forge-specific seam: it injects the metric-registry summary
 * labels. This is the pattern for the data/label-coupled finance adapters —
 * package owns the math, the product owns its label/registry wiring.
 *
 * Consumers keep importing from "@/lib/reports/adapters/year-end-giving-statements".
 * Forge's golden test (./__tests__/year-end-giving-statements.test.ts) guards
 * the composed surface.
 */
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";
import {
  aggregateYearEndGivingStatements,
  type YearEndGivingStatementsContext,
} from "@glorify-llc/accounting-core";

export type {
  YearEndGivingStatementsContext,
  GivingContribution,
  GivingPerson,
  GivingAllocation,
} from "@glorify-llc/accounting-core";

export function buildYearEndGivingStatementsAggregation(
  context: YearEndGivingStatementsContext,
): ReportAggregation {
  const { rows, statementsCount, totalAmount } =
    aggregateYearEndGivingStatements(context);

  return {
    asOf: context.asOf,
    summary: [
      buildReportSummaryItem("report.year_end.statements_count", statementsCount),
      buildReportSummaryItem("report.year_end.total_amount", totalAmount),
    ],
    rows,
  };
}
